// The microphone as both the voice conversation and dictation open it. What each does around it (cancelling a start,
// releasing what was built) stays with the hook, as the two call a start off differently.

// A static same-origin file: the CSP allows script-src 'self' only, so no blob: worklet module.
const CAPTURE_WORKLET_URL = `${process.env.PUBLIC_URL ?? ''}/voice-capture-worklet.js`;

export const openMicrophone = (): Promise<MediaStream> =>
    navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });

export interface CaptureNodes {
    source: MediaStreamAudioSourceNode;
    /** Posts each block the microphone hears to its port, as a Float32Array at the context's rate. */
    processor: AudioWorkletNode;
}

/**
 * Routes the microphone through the capture worklet. The context stays the caller's to close, also when this throws (an
 * unsupported sample rate, a worklet that did not load): a stop during the await must be able to close it.
 */
export const connectCapture = async (context: AudioContext, stream: MediaStream): Promise<CaptureNodes> => {
    const source = context.createMediaStreamSource(stream);
    await context.audioWorklet.addModule(CAPTURE_WORKLET_URL);
    const processor = new AudioWorkletNode(context, 'voice-capture');
    source.connect(processor);
    // Connected so the graph pulls the worklet; it writes nothing, so the output is silent.
    processor.connect(context.destination);
    return { source, processor };
};

/** How long a start waits for its audio to run: a resume the browser holds back for a click may never settle. */
export const AUDIO_START_WAIT_MS = 1_000;

/**
 * Whether the contexts run. One created without a click (a reconnect's timer) is not started by one, and Safari/iOS then
 * keeps it suspended: the worklet would post nothing while the button shows the microphone on.
 */
export const audioRuns = async (contexts: AudioContext[]): Promise<boolean> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
        Promise.all(contexts.map((context) => context.resume().catch(() => undefined))),
        new Promise<void>((resolve) => {
            timer = setTimeout(resolve, AUDIO_START_WAIT_MS);
        }),
    ]);
    clearTimeout(timer);
    return contexts.every((context) => context.state === 'running');
};
